import { PropType } from 'vue';
import { mapState } from 'vuex';
import { AccountInfo } from '@azure/msal-browser';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  EntrancePlantDetailDTO,
  EntrancePlantProRoles,
  EpUserRole,
  PlantSharingDTO,
} from '@/../client-generator/generated-clients/plants';
import { getUserInitialsForBubble } from '@/core/utils/utils';
import UserEPItem from './UserEPItem/UserEPItem.vue';
import ConfirmDeleteUser from '../../PlantDetail/MenuSharing/ConfirmDeleteUser/ConfirmDeleteUser.vue';
import i18n from '@/core/translations/i18n';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';

@Options({
  name: 'MenuEPSharing',
  props: {
    usersPermissions: {
      type: Object as PropType<EpUserRole[]>,
      required: true,
    },
  },
  watch: {
    isPopperVisible: function (newVal) {
      if(newVal) {
       this.newUserToShare.role = this.roleOptions[0].value;
      }
    }
  },
  components: {
    UserEPItem,
    ConfirmDeleteUser,
    DialogConfirmDelete,
  },
  emits: ['add-invited-user', 'remove-sharing-user'],
  computed: {
    ...mapState(['account', 'currentEntrancePlant', 'currentUserPermissions']),
  },
})
export default class MenuEPSharing extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  currentUserPermissions!: EpUserRole;
  usersPermissions!: EpUserRole[];
  getUserInitials = getUserInitialsForBubble;

  isLoading = false;
  isPopperVisible = false;
  isConfirmDeleteUserVisible = false;
  isConfirmRevokeRoleVisible = false;

  doShow = false;
  newUserToShare: { role: EntrancePlantProRoles; email: string } = {
    role: EntrancePlantProRoles.BuildingManager,
    email: '',
  };

  tempUser: EpUserRole | undefined = undefined;
  tempUserId?: string = '';
  account!: AccountInfo;

  get usersBubbles() {      
    if (this.usersPermissions.length > 3) {
      var shortenedList = this.usersPermissions.slice(0, 3);
      shortenedList.push(new EpUserRole({
        email: `+${this.usersPermissions.length - 3}`,
        roles: [],
      }));
      return shortenedList;
    }
    return this.usersPermissions;
  }

  get roleOptions() {
    const BuildingAdminRole = { 
          roleName: i18n.global.t('AppResourceEP.BuildingAdminRole'),
          value: EntrancePlantProRoles.BuildingManager,
        };
    const DeviceManagerRole = {
          roleName: i18n.global.t('AppResourceEP.DeviceManagerRole'),
          value: EntrancePlantProRoles.DeviceManager,
        };
    if (this.currentUserPermissions?.roles?.includes(EntrancePlantProRoles.DeviceManager) && 
      !this.currentUserPermissions?.roles?.includes(EntrancePlantProRoles.BuildingManager)) {
      return [DeviceManagerRole];
    }
    return [BuildingAdminRole, DeviceManagerRole];      
  }

  get isMeBuildingAdmin() {
    return (
      this.currentUserPermissions?.roles?.includes(
        EntrancePlantProRoles.BuildingManager
      ) ?? false
    );
  }

  get totBuildingAdmins() {
    const buildingAdmins = this.usersPermissions.filter((usr) =>
      usr.roles?.includes(EntrancePlantProRoles.BuildingManager)
    );
    return buildingAdmins.length;
  }

  get totDeviceManagers() {
    const deviceManagers = this.usersPermissions.filter((usr) =>
      usr.roles?.includes(EntrancePlantProRoles.DeviceManager)
    );
    return deviceManagers.length;
  }

  togglePopper() {
    this.isPopperVisible = !this.isPopperVisible;
  }

  async invitePerson() {
    this.isLoading = true;

    const newProUser = new EpUserRole({
      email: this.newUserToShare.email,
      roles: [this.newUserToShare.role],
    });

    try {
      await plantsClient.sharingproPOST(
        this.currentEntrancePlant.desktopId ?? -1,
        navigator.language,
        newProUser
      );

      this.$emit('add-invited-user', this.newUserToShare.email);
      this.newUserToShare.email = '';
    } catch (error) {
      console.log(error);
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
    }
  }

  async handleDeleteUser(user: EpUserRole) {
    this.toggleConfirmDeleteUser();
    this.tempUser = user;
    this.tempUserId = this.currentEntrancePlant.sharedWith?.find(
      (usr) => usr.email === user.email
    )?.userId;
  }

  handleRevokeRole(user: EpUserRole) {
    this.toggleConfirmRevokeRole();
    this.tempUser = new EpUserRole({
      email: user.email,
      roles: [EntrancePlantProRoles.BuildingManager],
    });
  }

  toggleConfirmRevokeRole() {
    this.isConfirmRevokeRoleVisible = !this.isConfirmRevokeRoleVisible;
    this.isPopperVisible = !this.isPopperVisible;
  }

  toggleConfirmDeleteUser() {
    this.isConfirmDeleteUserVisible = !this.isConfirmDeleteUserVisible;
    this.isPopperVisible = !this.isPopperVisible;
  }

  async revokeRole() {
    this.isLoading = true;
    try {
      await plantsClient.sharingproPUT(
        this.currentEntrancePlant.desktopId ?? -1,
        this.tempUser
      );
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
      this.tempUser = undefined;
    }
  }

  async deleteUser() {
    this.isLoading = true;

    try {
      await plantsClient.sharingproDELETE(
        this.currentEntrancePlant.desktopId ?? -1,
        this.tempUser
      );
      if (this.tempUserId === this.account.localAccountId) {
        this.$router.push({
          name: 'page-plants-hub',
        });
      } else {
        this.$emit('remove-sharing-user', this.tempUser);
      }
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.isLoading = false;
      this.tempUser = undefined;
      this.tempUserId = '';
    }
  }
}
